// Aviso legal del footer del ticket EDITABLE por el usuario. NO vive en la BD → se guarda en
// localStorage (por comercio, para no mezclar entre comercios en un equipo compartido). El
// default es `LEGAL_DISCLAIMER` (el texto de fábrica) → el comercio no tiene que escribirlo, solo
// editarlo si quiere. El builder del ticket recibe el texto resuelto vía config (no lee aquí).

import { useSyncExternalStore } from 'react';
import { LEGAL_DISCLAIMER } from '@utils/print/webble/ticketModel';

const KEY = 'webBleLegalDisclaimer';
const keyFor = (commerceId?: string | number | null): string => `${KEY}:${commerceId ?? 'default'}`;

// Suscriptores reactivos: las vistas previas se re-renderizan al editar el texto (useLegalDisclaimer).
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((l) => l());

// Texto de fábrica (default). Reexportado para que la UI muestre/restaure el mismo literal.
export const DEFAULT_LEGAL_DISCLAIMER = LEGAL_DISCLAIMER;

const readRaw = (commerceId?: string | number | null): string | null => {
  try {
    return typeof window !== 'undefined' ? (window.localStorage?.getItem(keyFor(commerceId)) ?? null) : null;
  } catch {
    return null;
  }
};

// Texto vigente: el override del comercio si existe y no está vacío; si no, el default.
export const getLegalDisclaimer = (commerceId?: string | number | null): string => {
  const v = readRaw(commerceId);
  return v && v.trim() ? v : LEGAL_DISCLAIMER;
};

// ¿El comercio tiene un texto PROPIO (distinto del default)? Para mostrar "restaurar default".
export const isLegalDisclaimerCustom = (commerceId?: string | number | null): boolean => {
  const v = readRaw(commerceId);
  return Boolean(v && v.trim() && v !== LEGAL_DISCLAIMER);
};

export const setLegalDisclaimer = (commerceId: string | number | null | undefined, text: string): void => {
  try {
    window.localStorage?.setItem(keyFor(commerceId), text);
  } catch {
    /* localStorage no disponible */
  }
  notify();
};

export const resetLegalDisclaimer = (commerceId?: string | number | null): void => {
  try {
    window.localStorage?.removeItem(keyFor(commerceId));
  } catch {
    /* localStorage no disponible */
  }
  notify();
};

// Hook reactivo: el texto vigente del aviso legal para un comercio. Se re-renderiza al editarlo
// (set/reset) → las vistas previas reflejan el cambio en vivo. El print path usa getLegalDisclaimer
// directo (no-React). SSR-safe: getServerSnapshot devuelve el default.
export const useLegalDisclaimer = (commerceId?: string | number | null): string =>
  useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => getLegalDisclaimer(commerceId),
    () => LEGAL_DISCLAIMER,
  );
